"use client";
import { env } from "@/env.mjs";
import type { BlokRetailer } from "@/storyblok";
import { APIProvider, Map as GoogleMap, Marker, InfoWindow } from "@vis.gl/react-google-maps";
import React, { useEffect, useRef, useState } from "react";
import styles from "../RetailersPage/RetailersPage.module.css";

interface MapProps {
	retailers?: BlokRetailer[];
	selectedRetailer?: BlokRetailer | null;
}

export const flyTo = async (
	map: google.maps.Map,
	target: google.maps.LatLngLiteral,
	options: {
		zoomLevel?: number;
		panDuration?: number;
	} = {},
) => {
	const { zoomLevel = 14, panDuration = 400 } = options;

	const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

	map.panTo(target);
	await sleep(panDuration);

	map.setZoom(zoomLevel);
};

// biome-ignore lint/suspicious/noShadowRestrictedNames: <explanation>
export const Map = ({ retailers, selectedRetailer }: MapProps) => {
	const mapRef = useRef<google.maps.Map | null>(null);
	const [isMapReady, setMapReady] = useState(false);
	const [activeMarker, setActiveMarker] = useState<BlokRetailer | null>(null);

	useEffect(() => {
		if (mapRef.current && isMapReady && retailers && retailers.length > 0) {
			const bounds = new google.maps.LatLngBounds();

			retailers.forEach((r) => {
				bounds.extend({
					lat: Number(r.latitude),
					lng: Number(r.longitude),
				});
			});

			if (!bounds.isEmpty()) {
				const map = mapRef.current;
				map.fitBounds(bounds, 100);

				// Limit max zoom level after fitting
				google.maps.event.addListenerOnce(map, "idle", () => {
					const currentZoom = map.getZoom() ?? 0;
					const maxZoom = 10;

					if (currentZoom > maxZoom) {
						map.setZoom(maxZoom);
					}
				});
			}
		}
	}, [isMapReady, retailers]);

	const lastCenterRef = useRef<{ lat: number; lng: number } | null>(null);

	useEffect(() => {
		if (selectedRetailer && mapRef.current) {
			const target = {
				lat: Number(selectedRetailer.latitude),
				lng: Number(selectedRetailer.longitude),
			};

			if (
				!lastCenterRef.current ||
				lastCenterRef.current.lat !== target.lat ||
				lastCenterRef.current.lng !== target.lng
			) {
				lastCenterRef.current = target;
				flyTo(mapRef.current, target);
			}
		}
	}, [selectedRetailer]);

	return (
		<APIProvider apiKey={env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}>
			<GoogleMap
				defaultCenter={{ lat: 50.1109, lng: 8.6821 }}
				defaultZoom={4.5}
				gestureHandling="greedy"
				disableDefaultUI
				onTilesLoaded={(event) => {
					const map = event.map as google.maps.Map;
					if (map && !mapRef.current) {
						mapRef.current = map;
						setMapReady(true);
					}
				}}
			>
				{retailers?.map((r) => (
					<Marker
						key={r.name}
						position={{
							lat: Number(r.latitude),
							lng: Number(r.longitude),
						}}
						title={r.name}
						onClick={() => setActiveMarker(r)}
					/>
				))}
				{activeMarker && (
					<InfoWindow
						position={{
							lat: Number(activeMarker.latitude),
							lng: Number(activeMarker.longitude),
						}}
						onCloseClick={() => setActiveMarker(null)}
					>
						<div className={styles.infoWindow}>
							<p className={styles.infoWindowTitle}>{activeMarker.name}</p>
							<p style={{ margin: 0 }}>{activeMarker.address as string}</p>
							<p style={{ margin: 0 }}>{activeMarker.phone}</p>
							<p style={{ margin: 0 }}>{activeMarker.email}</p>
						</div>
					</InfoWindow>
				)}
			</GoogleMap>
		</APIProvider>
	);
};
